import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const VIDEO_URL =
  'https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260328_083109_283f3553-e28f-428b-a723-d639c617eb2b.mp4';
// Aerial view of Mediterranean coastal city at golden hour — La Manga / Mar Menor area
const POSTER_URL =
  'https://images.unsplash.com/photo-1737781371561-66630b3402de?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1920';
const FADE_MS = 500;

export default function CinematicHero() {
  const videoRef = useRef(null);
  const animRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      video.style.opacity = '1';
      return;
    }

    let fadeOutTriggered = false;

    const cancelAnim = () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };

    const fadeIn = () => {
      cancelAnim();
      let start = null;
      const step = (ts) => {
        if (!start) start = ts;
        const p = Math.min((ts - start) / FADE_MS, 1);
        video.style.opacity = String(p);
        if (p < 1) {
          animRef.current = requestAnimationFrame(step);
        } else {
          fadeOutTriggered = false;
          animRef.current = requestAnimationFrame(monitor);
        }
      };
      animRef.current = requestAnimationFrame(step);
    };

    const fadeOut = () => {
      cancelAnim();
      let start = null;
      const step = (ts) => {
        if (!start) start = ts;
        const p = Math.min((ts - start) / FADE_MS, 1);
        video.style.opacity = String(1 - p);
        if (p < 1) animRef.current = requestAnimationFrame(step);
      };
      animRef.current = requestAnimationFrame(step);
    };

    const monitor = () => {
      if (video.duration && !fadeOutTriggered) {
        const timeLeft = video.duration - video.currentTime;
        if (timeLeft <= FADE_MS / 1000) {
          fadeOutTriggered = true;
          fadeOut();
          return;
        }
      }
      animRef.current = requestAnimationFrame(monitor);
    };

    const handleLoadedData = () => {
      video.style.opacity = '0';
      fadeIn();
    };

    const handleEnded = () => {
      video.style.opacity = '0';
      setTimeout(() => {
        video.currentTime = 0;
        video.play().catch(() => {});
        fadeIn();
      }, 100);
    };

    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('ended', handleEnded);
    video.style.opacity = '0';

    if (video.readyState >= 3) handleLoadedData();

    return () => {
      cancelAnim();
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('ended', handleEnded);
    };
  }, []);

  const scrollToAcceso = () => {
    const el = document.getElementById('acceso');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      data-testid="home-hero"
      className="relative min-h-screen bg-white overflow-hidden"
      aria-labelledby="hero-heading"
    >
      {/* Video background */}
      <video
        ref={videoRef}
        className="absolute object-cover w-full"
        style={{
          top: '300px',
          left: 0,
          right: 0,
          bottom: 0,
          height: 'calc(100% - 300px)',
          opacity: 0,
          zIndex: 0,
        }}
        src={VIDEO_URL}
        poster={POSTER_URL}
        autoPlay
        muted
        playsInline
        preload="auto"
        aria-hidden="true"
      />

      {/* Gradient overlay: white → transparent → white */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(to bottom, #ffffff 0%, #ffffff 20%, rgba(255,255,255,0.4) 38%, rgba(255,255,255,0) 52%, rgba(255,255,255,0) 68%, rgba(255,255,255,0.6) 82%, #ffffff 100%)',
          zIndex: 1,
        }}
        aria-hidden="true"
      />

      {/* Hero content */}
      <div
        className="relative z-10 flex flex-col items-center justify-start text-center px-6"
        style={{ paddingTop: 'calc(8rem + 20px)', paddingBottom: '10rem' }}
      >
        {/* Heading */}
        <h1
          id="hero-heading"
          className="font-display max-w-6xl anim-fade-rise"
          style={{
            fontSize: 'clamp(2.5rem, 7vw, 6rem)',
            lineHeight: '0.95',
            letterSpacing: '-2.46px',
            color: '#0A2540',
            fontWeight: 400,
          }}
        >
          Cuidemos Los Alcázares,{' '}
          <em style={{ color: '#1E5AA8', fontStyle: 'italic', fontWeight: 600 }}>compromiso</em>{' '}
          de{' '}
          <em style={{ color: '#1E5AA8', fontStyle: 'italic', fontWeight: 600 }}>todos</em>.
        </h1>

        {/* Description */}
        <p
          className="max-w-2xl mt-8 leading-relaxed anim-fade-rise-delay"
          style={{
            fontSize: 'clamp(1rem, 2vw, 1.125rem)',
            color: '#6F8AA8',
          }}
        >
          Plataforma digital del servicio municipal de recogida, limpieza viaria, playas y Ecoparque.
          Tres puertas de entrada según quién eres: gestión, técnicos municipales o ciudadanía.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 mt-12 anim-fade-rise-delay-2">
          <button
            data-testid="home-hero-primary-cta"
            onClick={scrollToAcceso}
            className="rounded-full px-12 py-4 text-base font-medium bg-[#0A2540] text-white hover:scale-[1.03] transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E5AA8] focus-visible:ring-offset-2"
            aria-label="Elegir mi acceso — ir al selector de rol"
          >
            Elegir mi acceso
          </button>
          <a
            data-testid="home-hero-secondary-cta"
            href="#servicio"
            className="rounded-full px-12 py-4 text-base font-medium border-2 border-[#0A2540] text-[#0A2540] hover:bg-[#CFE3F5] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E5AA8] focus-visible:ring-offset-2"
          >
            Conocer el servicio
          </a>
        </div>

        {/* Scroll hint */}
        <div className="mt-16 flex flex-col items-center gap-2 opacity-50" aria-hidden="true">
          <div className="w-px h-10 bg-[#6F8AA8] animate-bounce" />
          <span className="text-xs text-[#6F8AA8] tracking-widest uppercase">Desplaza</span>
        </div>
      </div>
    </section>
  );
}
